/* Keep the PDP gap measured from painted type, including after copy wraps. */
if (!customElements.get('illustrators-rhythm')) {
  customElements.define('illustrators-rhythm', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.il-section');
      if (!this.section) return;
      this.canvas = document.createElement('canvas').getContext('2d');
      this.metrics = new Map();
      this.imageEdges = new Map();
      this.paintedMetrics = new Map();
      this.raster = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
      this.abort = new AbortController();
      this.schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.measure());
      };
      this.observer = new ResizeObserver(this.schedule);
      for (let prior = this.section.previousElementSibling; prior; prior = prior.previousElementSibling) {
        if (prior.matches('.il-section')) this.observer.observe(prior);
      }
      const targets = this.section.matches('.il-section--process')
        ? [...(this.section.previousElementSibling?.querySelectorAll('.il-audience__card p') || [])]
        : [this.section.querySelector('.il-heading')];
      targets.filter(Boolean).forEach(target => {
        this.observer.observe(target);
        target.querySelectorAll('img').forEach(image => image.addEventListener('load', this.schedule, { signal: this.abort.signal }));
      });
      document.fonts.ready.then(() => { if (this.isConnected) this.schedule(); });
      document.fonts.addEventListener('loadingdone', () => { this.metrics.clear(); this.paintedMetrics.clear(); this.schedule(); }, { signal: this.abort.signal });
      window.addEventListener('resize', this.schedule, { signal: this.abort.signal });
      this.schedule();
    }
    ink(element) {
      let top = Infinity;
      let bottom = -Infinity;
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
      const range = document.createRange();
      let node;
      while ((node = walker.nextNode())) {
        const style = getComputedStyle(node.parentElement);
        const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        this.canvas.font = font;
        for (let index = 0; index < node.length; index++) {
          const character = node.textContent[index];
          if (/\s/.test(character)) continue;
          range.setStart(node, index);
          range.setEnd(node, index + 1);
          const rect = range.getBoundingClientRect();
          if (!rect.width || !rect.height) continue;
          const key = `${font}:${character}`;
          if (!this.metrics.has(key)) this.metrics.set(key, this.canvas.measureText(character));
          const metrics = this.metrics.get(key);
          if (!Number.isFinite(metrics.fontBoundingBoxAscent)) continue;
          const phaseX = rect.left - Math.floor(rect.left);
          const phaseY = rect.top - Math.floor(rect.top);
          const paintKey = `${key}:${style.color}:${phaseX.toFixed(3)}:${phaseY.toFixed(3)}`;
          let paint = this.paintedMetrics.get(paintKey);
          if (!paint) {
            const raster = this.raster;
            const fontBox = metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
            raster.canvas.width = Math.ceil(Math.max(rect.width, metrics.width)) + 6;
            raster.canvas.height = Math.ceil(fontBox) + 6;
            raster.fillStyle = '#fff9f6';
            raster.fillRect(0, 0, raster.canvas.width, raster.canvas.height);
            raster.font = font;
            raster.fillStyle = style.color;
            raster.fillText(character, 2 + phaseX, 2 + phaseY + metrics.fontBoundingBoxAscent + Math.max(0, fontBox - rect.height) / 2);
            paint = this.rasterEdges(raster);
            this.paintedMetrics.set(paintKey, paint);
          }
          top = Math.min(top, Math.floor(rect.top) + paint[0] - 2);
          bottom = Math.max(bottom, Math.floor(rect.top) + paint[1] - 2);
        }
      }
      // Decorative inline emoji never owns the primary heading's text-spacing edge.
      if (element.matches('.il-heading')) return { top, bottom };
      for (const image of element.querySelectorAll('img')) {
        if (!image.complete || !image.naturalWidth || !image.getClientRects().length) continue;
        const rect = image.getBoundingClientRect();
        const phaseX = rect.left - Math.floor(rect.left);
        const phaseY = rect.top - Math.floor(rect.top);
        const key = `${image.currentSrc}:${rect.width}:${phaseX}:${phaseY}`;
        let edge = this.imageEdges.get(key);
        if (!edge) {
          const canvas = document.createElement('canvas');
          canvas.width = Math.ceil(rect.width) + 6;
          canvas.height = Math.ceil(rect.height) + 6;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          try {
            context.fillStyle = '#fff9f6';
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.drawImage(image, 2 + phaseX, 2 + phaseY, rect.width, rect.height);
            edge = this.rasterEdges(context).map(value => value - 2 - phaseY);
          } catch {
            // Cross-origin replacements keep their ordinary image-box edge.
            edge = [0, rect.height];
          }
          this.imageEdges.set(key, edge);
        }
        top = Math.min(top, rect.top + edge[0]);
        bottom = Math.max(bottom, rect.top + edge[1]);
      }
      return { top, bottom };
    }
    rasterEdges(context) {
      const { width, height } = context.canvas;
      const pixels = context.getImageData(0, 0, width, height).data;
      let first = height;
      let last = 0;
      for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        const offset = (y * width + x) * 4;
        if (Math.min(pixels[offset], pixels[offset + 1], pixels[offset + 2]) < 170) {
          first = Math.min(first, y);
          last = Math.max(last, y + 1);
        }
      }
      return [first, last];
    }
    measure() {
      if (!this.isConnected || document.fonts.status !== 'loaded') return;
      if (this.paintedMetrics.size > 512) this.paintedMetrics.clear();
      if (this.imageEdges.size > 64) this.imageEdges.clear();
      if (this.section.matches('.il-section--process')) {
        const previous = this.section.previousElementSibling;
        const copy = [...(previous?.querySelectorAll('.il-audience__card p') || [])];
        if (!copy.length) return;
        const bottom = Math.max(...copy.map(element => this.ink(element).bottom));
        const tail = previous.getBoundingClientRect().bottom - bottom;
        if (Number.isFinite(tail)) this.setInset('--il-audience-ink-tail', tail);
      } else {
        const heading = this.section.querySelector('.il-heading');
        if (!heading) return;
        const inset = this.ink(heading).top - heading.getBoundingClientRect().top;
        if (Number.isFinite(inset)) this.setInset('--il-heading-ink-start', inset);
      }
    }
    setInset(property, value) {
      const inset = `${Math.round(value)}px`;
      if (this.section.style.getPropertyValue(property) === inset) return;
      this.section.style.setProperty(property, inset);
      this.schedule();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  });
}
